/**
 * Which features a drawn shape picks out.
 *
 * Client-side, against features the app has already fetched. A server-side
 * spatial query would be the better answer for a large layer — ArcGIS takes a
 * geometry directly on `query` — but it is not available for every source here,
 * and a selection that works the same way on all of them is worth more than one
 * that is exact on some and missing on others.
 */

/**
 * Ray casting: counts how many edges a ray from the point crosses.
 *
 * The `!==` on the two y-comparisons is the whole algorithm — it counts an
 * edge only when the ray passes BETWEEN its endpoints, which is what stops a
 * vertex lying exactly on the ray from being counted twice.
 *
 * A point exactly ON the boundary is undefined for ray casting and lands
 * inside here. That is the right side to fall on for a selection box: someone
 * who dragged the edge onto a feature meant to catch it.
 */
function pointInRing(point: [number, number], ring: number[][]): boolean {
  const [x, y] = point;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const crosses = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (crosses) inside = !inside;
  }
  return inside;
}

/** Inside the outer ring and outside every hole. */
export function pointInPolygon(point: [number, number], rings: number[][][]): boolean {
  if (rings.length === 0 || !pointInRing(point, rings[0])) return false;
  for (let index = 1; index < rings.length; index += 1) {
    if (pointInRing(point, rings[index])) return false; // in a hole
  }
  return true;
}

/** Every coordinate pair in a geometry, whatever its nesting. */
export function positionsOf(geometry: GeoJSON.Geometry | null | undefined): [number, number][] {
  const out: [number, number][] = [];
  const walk = (value: any) => {
    if (!Array.isArray(value)) return;
    if (typeof value[0] === 'number' && typeof value[1] === 'number') {
      out.push([value[0], value[1]]);
      return;
    }
    for (const part of value) walk(part);
  };
  walk((geometry as any)?.coordinates);
  return out;
}

/**
 * Whether a feature falls inside the drawn shape.
 *
 * A point is tested exactly. A line or polygon counts when ANY of its
 * vertices falls inside — not its centre, which would miss a long pipeline
 * whose middle sits outside the box while the stretch you dragged over does
 * not. That is the behaviour people expect from a selection box, and it is
 * the one that makes dragging over part of a network select that part.
 */
export function featureInShape(
  feature: GeoJSON.Feature,
  shapeRings: number[][][],
): boolean {
  const positions = positionsOf(feature.geometry);
  if (positions.length === 0) return false;
  return positions.some((position) => pointInPolygon(position, shapeRings));
}

/** The rings of a drawn selection shape, or null if it is not an area. */
export function shapeRings(geometry: GeoJSON.Geometry | null | undefined): number[][][] | null {
  if (!geometry || geometry.type !== 'Polygon') return null;
  const rings = geometry.coordinates as number[][][];
  return rings.length > 0 ? rings : null;
}

/**
 * Every coordinate of a geometry, for nearest-distance tests.
 *
 * Exposed separately from the shape test because "how far away is this?" and
 * "is this inside?" need the same walk over the coordinates, and a feature's
 * distance is the distance to its CLOSEST vertex — not to its centre, which
 * would put a pipeline running through your feet a kilometre away.
 */
export function nearestPositions(geometry: GeoJSON.Geometry | null | undefined): [number, number][] {
  return positionsOf(geometry);
}
